// src/pages/Settings/components/AccountSection.jsx
// STATUS: active | UPDATED: 2026-08-28
// LOG:
//   2026-08-28  Display name is editable here now. It was a read-only identity
//               summary, and the one field a person most wants to control, what
//               everyone calls them, could only be changed in the database.
//               Names now resolve through lib/names.js so this panel and the
//               header cannot disagree about who you are, which they did: this
//               file preferred full_name and the header preferred display_name.
//   2026-08-02  Header standardized; Phase 1 checkpoint committed (e765830).
// Identity summary, with the two fields people own about themselves.

import { useState } from 'react'
import {
  VStack, HStack, Text, Box, Flex, FormControl, FormLabel, FormHelperText,
  Input, Button, Alert, AlertIcon, useToast,
} from '@chakra-ui/react'
import SectionCard from './SectionCard.jsx'
import { displayNameFor, initialsFor } from '../../../lib/names.js'
import { updateOwnProfile } from '../../../lib/auth'
import { useAuth } from '../../../hooks/useAuth'

function Row({ label, value }) {
  return (
    <HStack justify="space-between" py={3} borderBottom="1px solid" borderColor="line" _last={{ borderBottom: 'none' }}>
      <Text fontSize="xs" fontWeight={600} color="inkMuted" letterSpacing="0.06em" textTransform="uppercase">
        {label}
      </Text>
      <Text fontSize="sm" color="ink" textAlign="right">
        {value || 'Not set'}
      </Text>
    </HStack>
  )
}

export default function AccountSection({ profile, user, dealership, role }) {
  const { refreshProfile } = useAuth()
  const toast = useToast()

  const [name, setName] = useState(profile?.display_name || '')
  const [inits, setInits] = useState(profile?.initials || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // What the rest of the app will call them, given what is typed right now.
  const shown = displayNameFor({ ...profile, display_name: name }, user)
  const shownInitials = initialsFor({ ...profile, display_name: name, initials: inits }, user)

  const dirty =
    (name || '') !== (profile?.display_name || '') ||
    (inits || '') !== (profile?.initials || '')

  const save = async (e) => {
    e.preventDefault()
    if (!user?.id || busy) return
    setError(null)
    setBusy(true)
    const { error: saveError } = await updateOwnProfile(user.id, { display_name: name, initials: inits })
    if (saveError) {
      setBusy(false)
      setError(saveError.message || 'Could not save. Try again.')
      return
    }
    // Refresh the shared profile so the header and the dashboard greeting
    // change at the same moment this panel does.
    await refreshProfile()
    setBusy(false)
    toast({ title: `Saved. We'll call you ${shown}.`, status: 'success', duration: 3000 })
  }

  return (
    <SectionCard title="Your account" description="How your name appears across the Academy.">
      <HStack spacing={4} mb={5}>
        <Flex w={12} h={12} borderRadius="full" bg="ink" color="onInk" align="center" justify="center" fontWeight={600} flexShrink={0}>
          {shownInitials}
        </Flex>
        <Box minW={0}>
          <Text fontSize="md" fontWeight={600} color="ink" noOfLines={1}>{shown}</Text>
          <Text fontSize="sm" color="inkMuted" textTransform="capitalize">{role || 'Member'}</Text>
        </Box>
      </HStack>

      <form onSubmit={save}>
        <VStack align="stretch" spacing={4}>
          {error && (
            <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{error}</Alert>
          )}

          <FormControl>
            <FormLabel>What would you like to be called?</FormLabel>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={profile?.first_name || 'Your name'}
              maxLength={60}
              autoComplete="nickname"
            />
            <FormHelperText>
              Used everywhere: the greeting on Today, the header, and your team's roster.
              Leave it empty to go by your full name.
            </FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>Initials</FormLabel>
            <Input
              value={inits}
              onChange={(e) => setInits(e.target.value.toUpperCase())}
              placeholder={initialsFor({ ...profile, initials: '' }, user)}
              maxLength={3}
              w="6.5rem"
              textTransform="uppercase"
            />
            <FormHelperText>Shown in your avatar. Leave empty and we'll build it from your name.</FormHelperText>
          </FormControl>

          <HStack>
            <Button type="submit" size="md" isLoading={busy} loadingText="Saving" isDisabled={!dirty}>
              Save name
            </Button>
          </HStack>
        </VStack>
      </form>

      <VStack align="stretch" spacing={0} mt={6}>
        <Row label="Username" value={profile?.username} />
        <Row label="Email" value={user?.email} />
        <Row label="Dealership" value={dealership?.name} />
      </VStack>
    </SectionCard>
  )
}
